import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchSectionSkills, getCurrentUserId } from '../../SkillsApi/SkillsApi'
import './CoursePage.css'
import ModuleOne from "./CourseImage/moduleOne.webp"
import ModuleTwo from "./CourseImage/moduleTwo.webp"
import ModuleThree from "./CourseImage/moduleThree.webp"
import ModuleFour from "./CourseImage/moduleFour.webp"
import ModuleFive from "./CourseImage/moduleFive.webp"

const SECTION = 'DroneSection'

const COURSE_ITEMS = [
  {
    id: 1,
    imageUrl: ModuleOne,
    title: 'Module 1: Getting Started',
    description: 'Kickstart your journey with the fundamentals you need before diving deeper.',
    route: '/module_one_drone_intro',
  },
  {
    id: 2,
    imageUrl: ModuleTwo,
    title: 'Module 2: Core Concepts',
    description: 'Build on the basics and understand the core ideas that drive everything else.',
    route: '/module_one_drone_level_two',
  },
  {
    id: 3,
    imageUrl: ModuleThree,
    title: 'Module 3: Deep Dive',
    description: 'Go beyond the surface with a detailed, hands-on exploration of the topic.',
    // route: '/thirdvideo',
  },
  {
    id: 4,
    imageUrl: ModuleFour,
    title: 'Module 4: Applied Practice',
    description: 'Apply what you have learned through real, practical scenarios.',
    route: '/fourthvideo',
  },
  {
    id: 5,
    imageUrl: ModuleFive,
    title: 'Module 5: Final Wrap-up',
    description: 'Bring everything together and complete your learning path.',
    route: '/fifthvideo',
  },
]

const CoursePage = () => {
  const navigate = useNavigate()
  const [moduleProgress, setModuleProgress] = useState({})
  const [loading, setLoading] = useState(true)

  // Re-fetches progress from MongoDB every time the page mounts, so
  // coming back from a just-finished module instantly reflects the
  // newly unlocked card — no stale local cache involved.
  useEffect(() => {
    const userId = getCurrentUserId()

    if (!userId) {
      setLoading(false)
      return
    }

    fetchSectionSkills(userId, SECTION)
      .then((data) => {
        setModuleProgress(data && typeof data === 'object' ? data : {})
        setLoading(false)
      })
      .catch((err) => {
        console.error('fetchSectionSkills failed:', err)
        setModuleProgress({})
        setLoading(false)
      })
  }, [])

  const getPercentFor = (id) => moduleProgress?.[`module${id}`]?.percent ?? 0

  const isCompleted = (id) => getPercentFor(id) >= 100

  const isUnlocked = (id) => id === 1 || isCompleted(id - 1)

  const goToModule = (route) => {
    navigate(route)
  }

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      document.querySelector("#root")?.scrollIntoView({ behavior: "smooth" })
    })
    return () => cancelAnimationFrame(id)
  }, [])

  return (
    <section className="upskld_course_pg_wrapper">
      <div className="upskld_course_pg_header">
        <h2 className="upskld_course_pg_heading">Your Learning Path</h2>
        <p className="upskld_course_pg_subheading">
          Complete each module in order to unlock the next one.
        </p>
      </div>

      {loading ? (
        <p className="upskld_course_pg_loading">Loading your course...</p>
      ) : (
        <div className="upskld_course_pg_grid">
          {COURSE_ITEMS.map((item, index) => {
            const unlocked = isUnlocked(item.id)
            const isDone = isCompleted(item.id)
            const isAboveFold = index === 0

            return (
              <div
                key={item.id}
                className={`upskld_course_pg_card ${unlocked ? '' : 'upskld_course_pg_card_locked'}`}
              >
                <div className="upskld_course_pg_card_inner">
                  <div className="upskld_course_pg_image_wrap">
                    <img
                      src={item.imageUrl}
                      alt={`upskld ${item.title}`}
                      className="upskld_course_pg_image"
                      width="400"
                      height="240"
                      loading={isAboveFold ? "eager" : "lazy"}
                      decoding="async"
                      fetchpriority={isAboveFold ? "high" : "low"}
                    />
                    {isDone && (
                      <span className="upskld_course_pg_done_badge">
                        &#10003; Completed
                      </span>
                    )}
                  </div>

                  <h3 className="upskld_course_pg_card_title">{item.title}</h3>
                  <p className="upskld_course_pg_card_desc">{item.description}</p>

                  {unlocked && (
                    <button
                      type="button"
                      className="upskld_course_pg_watch_btn"
                      onClick={() => goToModule(item.route)}
                    >
                      Watch Video
                    </button>
                  )}
                </div>

                {!unlocked && (
                  <div className="upskld_course_pg_lock_overlay">
                    <span className="upskld_course_pg_lock_icon">&#128274;</span>
                    <span className="upskld_course_pg_upcoming_badge">UPCOMING</span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </section>
  )
}

export default CoursePage